// src/componente/Rapoarte/RaportPrivireGenerala.jsx
import React, { useEffect, useState } from "react";
import { Pie, Bar, Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../pagini/Firebase";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

import "./Stiluri/RaportPrivireGenerala.css";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  ChartDataLabels,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement
);

const RaportPrivireGenerala = () => {
  const [totalBeneficiari, setTotalBeneficiari] = useState(0);
  const [beneficiariNoiLuna, setBeneficiariNoiLuna] = useState(0);

  const [distributieJudet, setDistributieJudet] = useState({ labels: [], datasets: [] });
  const [beneficiariVarste, setBeneficiariVarste] = useState({ labels: [], datasets: [] });
  const [evolutieLunara, setEvolutieLunara] = useState({ labels: [], datasets: [] });
  const [evolutieLunaraPeJudete, setEvolutieLunaraPeJudete] = useState({ labels: [], datasets: [] });
  const [distributieSex, setDistributieSex] = useState({ labels: [], datasets: [] });
  const [distributieJudetResedinta, setDistributieJudetResedinta] = useState(null);

  useEffect(() => {
    async function fetchData() {
      // Preluare toți beneficiarii fără filtrare
      const beneficiariSnap = await getDocs(collection(db, "Beneficiari"));
      const beneficiari = beneficiariSnap.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          dataAdaugare: data.dataAdaugare
            ? (typeof data.dataAdaugare.toDate === "function"
                ? data.dataAdaugare.toDate()
                : new Date(data.dataAdaugare))
            : null,
        };
      });

      // TOTAL național
      setTotalBeneficiari(beneficiari.length);

      // Beneficiari noi luna curenta
      const azi = new Date();
      const lunaCurenta = azi.getMonth() + 1;
      const anCurent = azi.getFullYear();
      const beneficiariNoi = beneficiari.filter(b => {
        if (!b.dataAdaugare) return false;
        return b.dataAdaugare.getMonth() + 1 === lunaCurenta &&
               b.dataAdaugare.getFullYear() === anCurent;
      });
      setBeneficiariNoiLuna(beneficiariNoi.length);

      // Distribuție pe județe (ultima locație)
      const judetCount = {};
      beneficiariSnap.docs.forEach((doc) => {
        const b = doc.data();
        const judet = b?.ultimaLocatie?.judet || "Necunoscut";
        judetCount[judet] = (judetCount[judet] || 0) + 1;
      });
      setDistributieJudetResedinta({
        labels: Object.keys(judetCount),
        datasets: [
          {
            label: "Număr beneficiari",
            data: Object.values(judetCount),
            backgroundColor: [
              "#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0", "#9966FF", "#FF9F40",
            ],
          },
        ],
      });

      // Distribuție pe județe (Pie) folosind JudetSector
      const judeteCount = {};
      beneficiari.forEach((b) => {
        const judet = b.JudetSector;
        if (judet) {
          judeteCount[judet] = (judeteCount[judet] || 0) + 1;
        }
      });
      const judeteLabels = Object.keys(judeteCount);
      const judeteData = Object.values(judeteCount);
      const colors = [
        "#007bff", "#28a745", "#ffc107", "#dc3545", "#6f42c1",
        "#fd7e14", "#20c997", "#6610f2", "#e83e8c", "#17a2b8"
      ];
      setDistributieJudet({
        labels: judeteLabels,
        datasets: [{
          data: judeteData,
          backgroundColor: judeteLabels.map((_, i) => colors[i % colors.length]),
          hoverBackgroundColor: judeteLabels.map((_, i) => colors[i % colors.length]),
        }],
      });

      // Beneficiari pe categorii de vârstă
      const varsteBuckets = { "0-18": 0, "19-35": 0, "36-60": 0, "60+": 0 };
      const calcVarstaDinString = (dataNasteriiStr) => {
        if (!dataNasteriiStr) return null;
        const dn = new Date(dataNasteriiStr);
        const azi = new Date();
        let varsta = azi.getFullYear() - dn.getFullYear();
        const lunaDiff = azi.getMonth() - dn.getMonth();
        const ziDiff = azi.getDate() - dn.getDate();
        if (lunaDiff < 0 || (lunaDiff === 0 && ziDiff < 0)) varsta--;
        return varsta;
      };
      beneficiari.forEach((b) => {
        const v = calcVarstaDinString(b.data_nasterii);
        if (v !== null) {
          if (v <= 18) varsteBuckets["0-18"]++;
          else if (v <= 35) varsteBuckets["19-35"]++;
          else if (v <= 60) varsteBuckets["36-60"]++;
          else varsteBuckets["60+"]++;
        }
      });
      setBeneficiariVarste({
        labels: Object.keys(varsteBuckets),
        datasets: [{
          label: "Beneficiari",
          data: Object.values(varsteBuckets),
          backgroundColor: ["#007bff", "#28a745", "#ffc107", "#dc3545"],
        }],
      });

      // Evoluție lunară simplă (6 luni)
      const lunaCurentaData = new Date();
      const luniLabels = [];
      for (let i = 5; i >= 0; i--) {
        const lunaData = new Date(lunaCurentaData.getFullYear(), lunaCurentaData.getMonth() - i, 1);
        luniLabels.push(lunaData.toLocaleString("ro-RO", { month: "short", year: "numeric" }));
      }
      const lunaBeneficiari = luniLabels.map((_, idx) => {
        const lunaData = new Date(lunaCurentaData.getFullYear(), lunaCurentaData.getMonth() - (5 - idx), 1);
        return beneficiari.filter(b => {
          if (!b.dataAdaugare) return false;
          const d = b.dataAdaugare;
          return d.getMonth() === lunaData.getMonth() && d.getFullYear() === lunaData.getFullYear();
        }).length;
      });
      setEvolutieLunara({
        labels: luniLabels,
        datasets: [{
          label: "Beneficiari noi",
          data: lunaBeneficiari,
          borderColor: "#007bff",
          backgroundColor: "rgba(0, 123, 255, 0.3)",
          fill: true,
          tension: 0.3,
        }],
      });

      // Evoluție lunară pe județe
      const judeteDistincte = [...new Set(beneficiari.map(b => b.locatie?.judet).filter(j => !!j))];
      const culoriLinie = [
        "#007bff", "#28a745", "#ffc107", "#dc3545", "#6f42c1",
        "#fd7e14", "#20c997", "#6610f2", "#e83e8c", "#17a2b8",
        "#6610f2", "#20c997", "#fd7e14"
      ];
      const datasetsJudete = judeteDistincte.map((judet, index) => {
        const dataLunara = luniLabels.map((_, idx) => {
          const lunaData = new Date(lunaCurentaData.getFullYear(), lunaCurentaData.getMonth() - (5 - idx), 1);
          return beneficiari.filter(b => {
            if (!b.dataAdaugare) return false;
            const d = b.dataAdaugare;
            return d.getMonth() === lunaData.getMonth() &&
                   d.getFullYear() === lunaData.getFullYear() &&
                   b.locatie?.judet === judet;
          }).length;
        });
        return {
          label: judet,
          data: dataLunara,
          borderColor: culoriLinie[index % culoriLinie.length],
          backgroundColor: "transparent",
          fill: false,
          tension: 0.3,
        };
      });
      setEvolutieLunaraPeJudete({ labels: luniLabels, datasets: datasetsJudete });

      // Distribuție pe sex
      const sexCount = {};
      beneficiari.forEach((b) => {
        const sex = b.Sex || b.gen || "Necunoscut";
        sexCount[sex] = (sexCount[sex] || 0) + 1;
      });
      const sexLabels = Object.keys(sexCount);
      const sexData = Object.values(sexCount);
      const sexColors = ["#007bff", "#dc3545", "#6c757d"];
      setDistributieSex({
        labels: sexLabels,
        datasets: [{
          data: sexData,
          backgroundColor: sexLabels.map((_, i) => sexColors[i % sexColors.length]),
          hoverBackgroundColor: sexLabels.map((_, i) => sexColors[i % sexColors.length]),
        }],
      });
    }

    fetchData();
  }, []); // Fără dependențe pe interval, se iau toate datele

  const exportPDF = (selector, filename) => {
    const input = document.querySelector(selector);
    if (!input) return;
    html2canvas(input, { scale: 2 }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 0, pdfWidth, pdfHeight);
      pdf.save(filename);
    });
  };

  return (
    <div className="dashboard-container-rapoarte">
      <div className="raport-intro">
        <h1 className="raport-intro-titlu">Date statistice și indicatori naționali</h1>
        <p className="raport-intro-subtitlu">
          Pentru a obține date la nivel local, vă rugăm să consultați categoria aferentă din meniul lateral
        </p>
      </div>

      <section className="kpi-cards">
        <div className="kpi-card">
          <h3>Total Beneficiari</h3>
          <p>{totalBeneficiari}</p>
        </div>
        <div className="kpi-card">
          <h3>Beneficiari Noi/Lună Curentă</h3>
          <p>{beneficiariNoiLuna}</p>
        </div>
      </section>

      <section className="grafice-rapoarte">
        <div className="chart-card" id="chart-judete" style={{ height: "350px" }}>
          <h4>Distribuție Beneficiari (Județe)</h4>
          <Bar
            data={distributieJudet}
            options={{
              plugins: {
                legend: { display: false },
                datalabels: { anchor: "end", align: "top", color: "#000", formatter: (v) => v },
              },
              scales: {
                y: { beginAtZero: true, ticks: { stepSize: 1 }, title: { display: true, text: "Număr beneficiari" } },
                x: { ticks: { autoSkip: false }, title: { display: true, text: "Județe" } },
              },
              responsive: true,
              maintainAspectRatio: false,
            }}
          />
        </div>

        {distributieJudetResedinta && (
          <div className="chart-card" id="chart-judete-resedinta" style={{ height: "350px" }}>
            <h4>Prezență Beneficiari (Ultima locație)</h4>
            <Bar
              data={distributieJudetResedinta}
              options={{
                responsive: true,
                plugins: { legend: { display: false }, datalabels: { anchor: "end", align: "top" } },
              }}
            />
          </div>
        )}

        <div className="chart-card" id="chart-varste">
          <h4>Beneficiari pe Categorii de Vârstă</h4>
          <Bar
            data={beneficiariVarste}
            options={{ plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true } } }}
          />
        </div>

        <div className="chart-card" id="chart-evolutie" style={{ height: "300px", width: "100%" }}>
          <h4>Evoluție Lunara Beneficiari</h4>
          <Line data={evolutieLunara} options={{ plugins: { legend: { position: "top" } }, scales: { y: { beginAtZero: true } }, maintainAspectRatio: false }} />
        </div>

        <div className="chart-card" id="chart-evolutie-judete" style={{ height: "400px", width: "100%" }}>
          <h4>Evoluție lunară Beneficiari pe Județe</h4>
          <Line
            data={evolutieLunaraPeJudete}
            options={{
              plugins: { legend: { position: "right", labels: { boxWidth: 12, padding: 6 } }, tooltip: { mode: "index", intersect: false } },
              responsive: true,
              maintainAspectRatio: false,
              scales: { y: { beginAtZero: true, ticks: { stepSize: 1 } } },
              interaction: { mode: "nearest", axis: "x", intersect: false },
            }}
          />
        </div>

        <div className="chart-card" id="chart-sex">
          <h4>Distribuție Beneficiari pe Sex</h4>
          <Pie data={distributieSex} options={{ plugins: { datalabels: { color: "#fff", formatter: (v) => v }, legend: { position: "bottom" } } }} />
        </div>
      </section>

      <button className="btn btn-primary" onClick={() => exportPDF(".dashboard-container-rapoarte", "Raport_PrivireGenerala.pdf")} style={{ marginTop: "1rem" }}>
        Export PDF
      </button>
    </div>
  );
};

export default RaportPrivireGenerala;
